import { getCartItemBasePricePerGuest, readSessionCheckoutTax } from '@/utils/specialExtra';
import { normalizeOptionalQuantities } from '@/utils/optionalOverrides';
import { readTargetEventId } from '@/utils/reusableOrder';
import { useCallback, useRef } from "react";
import { isRealEventType } from '@/utils/eventType';
import { supabase } from "@/integrations/supabase/client";
import { CartItem } from "@/utils/cartStorage";
import { buildSpecialGuestsSnapshot } from "@/utils/specialGuests";
import { freightExtraColumns } from "@/utils/freightExtra";
import { getQuoteOnlySelected } from "@/utils/quoteVisibility";

const isValidUUID = (val: string | null | undefined): boolean => {
  if (!val) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
};

const generateShortEventCode = (supplierName?: string): string => {
  const clean = (supplierName || "AME")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z]/g, "")
    .toUpperCase();
  const prefix = (clean.slice(0, 3) || "AME").padEnd(3, "X");
  const hex = Math.random().toString(16).slice(2, 6).toUpperCase();
  return `${prefix}-${hex}`;
};

export interface QuoteDraftParentData {
  cep: string;
  eventDate: string;
  guests: string;
  supplierId?: string;
  userId?: string;
  supplierName?: string;
  createdBySupplierId?: string;
}

/**
 * Convidados, data e CEP do rascunho pai a partir do carrinho do fornecedor. Sem isso o pai ficava com os dados do
 * início da jornada (ex.: 57 convidados) enquanto os itens já estavam em 60, e a proposta saía com 57 (MAR-8A91).
 */
export const draftParentEventFields = (
  cartItems: Pick<CartItem, 'guests' | 'cep' | 'date'>[],
): { p_guests: string | null; p_cep: string | null; p_event_date: string | null } => {
  const first = cartItems.find((i) => i.guests || i.cep || i.date) ?? cartItems[0];
  return {
    p_guests: first?.guests ? String(first.guests) : null,
    p_cep: first?.cep || null,
    p_event_date: first?.date ? String(first.date).split("T")[0] : null,
  };
};

type DraftFinancialSummary = { discount_code?: string | null; discount_value?: number | null; freight?: number | null } | undefined;

/** Quem chama mexeu no cupom quando manda um valor (código ou desconto, inclusive 0 ao remover). */
export const isCouponTouched = (summary: DraftFinancialSummary): boolean =>
  !!summary && (
    (summary.discount_code !== undefined && summary.discount_code !== null) ||
    (summary.discount_value !== undefined && summary.discount_value !== null)
  );

/**
 * Total do rascunho da proposta. Sem cupom/frete nesta gravação, o banco MANTÉM o que já estava gravado (o RPC
 * usa COALESCE), então o total usa esses mesmos valores. Antes ficava cupom gravado e total sem o desconto
 * (VEI-4600: itens R$ 5.700, desconto R$ 285, total R$ 5.700 em vez de R$ 5.415).
 */
export const resolveDraftTotals = (
  subtotal: number,
  summary: DraftFinancialSummary,
  persisted: { delivery_fee?: number | null; discount_value?: number | null } | null,
): { couponTouched: boolean; freight: number; discountValue: number; total: number } => {
  const couponTouched = isCouponTouched(summary);
  const freight = Number(summary?.freight ?? persisted?.delivery_fee ?? 0) || 0;
  const discountValue = couponTouched
    ? Number(summary?.discount_value ?? 0) || 0
    : Number(persisted?.discount_value ?? 0) || 0;
  return { couponTouched, freight, discountValue, total: Math.max(0, subtotal + freight - discountValue) };
};

/**
 * Helper: call the security-definer RPC `upsert_quote_draft`.
 * Bypasses RLS issues by validating supplier ownership server-side.
 */
const callUpsertDraftRpc = async (params: Record<string, any>): Promise<{ data: string | null; error: any }> => {
  const { data, error } = await (supabase as any).rpc("upsert_quote_draft", params);
  return { data, error };
};

/**
 * Hook for persisting quote drafts to the `quote_drafts` table.
 * Uses a SECURITY DEFINER RPC to bypass RLS subquery issues.
 * Used exclusively in `?mode=orcamento` (supplier quote mode).
 * On share, drafts are promoted to `events` with an event_code.
 */
export type PromoteErrorCode = "draft_not_found" | "empty_quote" | "network" | "unknown";

export interface PromoteError {
  code: PromoteErrorCode;
  message: string;
}

/** Mensagem amigável (pt-BR) para cada motivo de falha na promoção do rascunho. */
export const describePromoteError = (error: PromoteError | null): string => {
  switch (error?.code) {
    case "draft_not_found":
      return "A proposta expirou nesta sessão. Recriamos o rascunho — toque em compartilhar novamente.";
    case "empty_quote":
      return "A proposta está sem itens sincronizados. Reabra o carrinho e tente compartilhar de novo.";
    case "network":
      return "Falha de conexão ao gerar o link. Verifique a internet e tente novamente.";
    default:
      return "Não foi possível gerar o link da proposta. Tente novamente.";
  }
};

export const useQuoteDraftPersistence = () => {
  const draftParentIdRef = useRef<string | null>(null);
  const lastPromoteErrorRef = useRef<PromoteError | null>(null);
  const syncQueueRef = useRef<Promise<void>>(Promise.resolve());


  const upsertDraftParent = useCallback(async (data: QuoteDraftParentData): Promise<string | null> => {
    try {
      if (!draftParentIdRef.current) {
        const stored = sessionStorage.getItem('ameup_draft_parent_id');
        if (stored) draftParentIdRef.current = stored;
      }

      const draftId = draftParentIdRef.current || crypto.randomUUID();
      const isUpdate = !!draftParentIdRef.current;

      const rpcParams: Record<string, any> = {
        p_id: draftId,
        p_cep: data.cep || null,
        p_event_date: data.eventDate || null,
        p_guests: data.guests || null,
        p_supplier_id: isValidUUID(data.supplierId) ? data.supplierId : null,
        p_user_id: isValidUUID(data.userId) ? data.userId : null,
        p_created_by_supplier_id: isValidUUID(data.createdBySupplierId)
          ? data.createdBySupplierId
          : isValidUUID(data.supplierId) ? data.supplierId : null,
        p_status: "Rascunho",
        p_is_update: isUpdate,
      };

      const { error } = await callUpsertDraftRpc(rpcParams);
      if (error) {
        console.error("❌ RPC upsert_quote_draft failed:", error.message, error.code);
        // Fallback: try without user_id
        if (rpcParams.p_user_id) {
          rpcParams.p_user_id = null;
          const { error: retryErr } = await callUpsertDraftRpc(rpcParams);
          if (retryErr) throw retryErr;
        } else {
          throw error;
        }
      }

      draftParentIdRef.current = draftId;
      sessionStorage.setItem('ameup_draft_parent_id', draftId);
      return draftId;
    } catch (err) {
      console.error("❌ Error upserting draft parent:", err);
      return null;
    }
  }, []);

  const syncCartToDrafts = useCallback(async (
    cartItems: CartItem[],
    parentDraftId: string,
    userId?: string,
    financialSummary?: { discount_code?: string; discount_value?: number; freight?: number; subtotal?: number; total?: number }
  ) => {
    const runSync = async () => {
      try {


      if (!cartItems.length) {
        console.warn("⚠️ syncCartToDrafts called with empty cart");
        return;
      }

      const desiredOrigens = new Set<string>();
      cartItems.forEach((item) => {
        if (item.isSelectableMenu && item.selectedMenuItems && Object.keys(item.selectedMenuItems).length > 0) {
          Object.keys(item.selectedMenuItems).forEach((childItemId) => {
            desiredOrigens.add(`${item.productId}:${childItemId}`);
          });
        } else {
          desiredOrigens.add(item.productId);
        }
      });

      // Deactivate only children that are no longer present. Reusing existing rows
      // avoids the old deactivate-all/reinsert flow, which could let a slower
      // autosave overwrite a newer substitution/category selection.
      const { data: existingChildren } = await (supabase as any)
        .from("quote_drafts")
        .select("id, origem")
        .eq("parent_event_id", parentDraftId)
        .eq("is_active", true);

      const obsoleteChildren = (existingChildren || []).filter((child: any) => !desiredOrigens.has(child.origem));
      
      if (obsoleteChildren.length > 0) {
        await (supabase as any)
          .from("quote_drafts")
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .in("id", obsoleteChildren.map((c: any) => c.id));
      }

      // Upsert each cart item as a child draft row via RPC
      for (const item of cartItems) {
        if (item.isSelectableMenu && item.selectedMenuItems && Object.keys(item.selectedMenuItems).length > 0) {
          const selectedMenuItems = item.selectedMenuItems;
          const childItemIds = Object.keys(selectedMenuItems);
          const parentProductId = item.productId;

          // Fetch child item details from supplier_menu for better metadata
          const { data: childMenuData } = await (supabase as any)
            .from("supplier_menu")
            .select("id, name, fixed_price, event_hour_time, category")
            .in("id", childItemIds);

          const childMenuMap = new Map<string, any>();
          (childMenuData || []).forEach((m: any) => childMenuMap.set(m.id, m));

          for (const childItemId of childItemIds) {
            const quantity = selectedMenuItems[childItemId] || 1;
            const menuInfo = childMenuMap.get(childItemId);
            const itemName = menuInfo?.name || 'Item selecionado';
            const itemPrice = (menuInfo?.fixed_price || 0) * quantity;
            const itemEventHourTime = menuInfo?.event_hour_time || item.eventHourTime || null;
            const origem = `${parentProductId}:${childItemId}`;
            const childSupplierId = isValidUUID(item.supplierId) ? item.supplierId : null;

            // Check for existing child draft
            const { data: existing } = await (supabase as any)
              .from("quote_drafts")
              .select("id")
              .eq("parent_event_id", parentDraftId)
              .eq("origem", origem)
              .order("updated_at", { ascending: false })
              .limit(1);

            const childId = existing && existing.length > 0 ? existing[0].id : crypto.randomUUID();
            const isChildUpdate = !!(existing && existing.length > 0);

            await callUpsertDraftRpc({
              p_id: childId,
              p_parent_event_id: parentDraftId,
              p_origem: origem,
              p_supplier_id: childSupplierId,
              p_created_by_supplier_id: childSupplierId,
              p_user_id: isValidUUID(userId) ? userId : null,
              p_menu: itemName,
              p_party_time: itemEventHourTime,
              p_selected_categories: [item.category],
              p_calculated_category: itemPrice.toString(),
              p_calculated_guest: (menuInfo?.fixed_price || 0).toString(),
              p_calculated_total: itemPrice || 0,
              p_total_value: itemPrice || 0,
              p_item_quantity: quantity.toString(),
              p_substitutions: item.substitutions && Object.keys(item.substitutions).length > 0
                ? item.substitutions
                : {},
              p_guests: item.guests || null,
              p_cep: item.cep || null,
              p_event_date: item.date ? item.date.split("T")[0] : null,
              // Only set status when creating; let updates preserve existing status
              p_status: isChildUpdate ? null : "Rascunho",
              p_is_update: isChildUpdate,
              p_is_active: true,
            });
          }
          continue;
        }

        const productId = item.productId;
        const origem = productId;
        const childSupplierId = isValidUUID(item.supplierId) ? item.supplierId : null;

        // Check for existing child
        const { data: existing } = await (supabase as any)
          .from("quote_drafts")
          .select("id")
          .eq("parent_event_id", parentDraftId)
          .eq("origem", origem)
          .order("updated_at", { ascending: false })
          .limit(1);

        const childId = existing && existing.length > 0 ? existing[0].id : crypto.randomUUID();
        const isChildUpdate = !!(existing && existing.length > 0);

        const rpcParams: Record<string, any> = {
          p_id: childId,
          p_parent_event_id: parentDraftId,
          p_origem: origem,
          p_supplier_id: childSupplierId,
          p_created_by_supplier_id: childSupplierId,
          p_user_id: isValidUUID(userId) ? userId : null,
          p_menu: item.itemName || item.productName || null,
          p_party_time: item.eventHourTime || null,
          p_event_hour_is_max: item.eventHourIsMax ?? null,
          p_supplier_arrival_time: item.hourArrived || null,
          p_selected_categories: [item.category],
          p_calculated_category: item.totalPrice.toString(),
          p_calculated_guest: (item.pricePerPerson || 0).toString(),
          p_calculated_total: item.totalPrice || 0,
          p_total_value: item.totalPrice || 0,
          p_item_quantity: (item.selectedItemsCount || 1).toString(),
          p_substitutions: item.substitutions && Object.keys(item.substitutions).length > 0
            ? item.substitutions
            : (item.categorySelections && Object.values(item.categorySelections).some(arr => arr.length > 0) ? item.categorySelections : {}),
          p_guests: item.guests || null,
          p_cep: item.cep || null,
          p_event_date: item.date ? item.date.split("T")[0] : null,
          // Only set status when creating; let updates preserve existing status
          p_status: isChildUpdate ? null : "Rascunho",
          p_is_update: isChildUpdate,
          p_is_active: true,
        };

        const { error } = await callUpsertDraftRpc(rpcParams);
        if (error) {
          if (rpcParams.p_user_id) {
            rpcParams.p_user_id = null;
            await callUpsertDraftRpc(rpcParams);
          }
        }
      }

      // Update parent totals + aggregated fields via RPC
      const subtotal = cartItems.reduce((sum, i) => sum + i.totalPrice, 0);
      const allCategories = [...new Set(cartItems.map(i => i.category).filter(Boolean))];
      const totalGuests = parseInt(cartItems[0]?.guests || '0', 10);
      const pricePerGuest = totalGuests > 0 ? (subtotal / totalGuests) : 0;
      const menuSummary = cartItems.map(i => {
        const base: Record<string, any> = {
          productId: i.productId,
          name: i.itemName || i.productName,
          category: i.category,
          totalPrice: i.totalPrice,
          quantity: i.selectedItemsCount || 1,
        };
        if (i.substitutions && Object.keys(i.substitutions).length > 0) {
          base.substitutions = i.substitutions;
        }
        // Itens incluídos pelo fornecedor na proposta (mode=orcamento)
        if (i.additions && Object.keys(i.additions).length > 0) {
          base.additions = i.additions;
        }

        // Selectable menus (e.g. "escolha 3 de 10") — persist the actual chosen items
        // so the contract Clause 6 can show ONLY what the client selected.
        if (i.isSelectableMenu && i.selectedMenuItems && Object.keys(i.selectedMenuItems).length > 0) {
          base.isSelectableMenu = true;
          base.selectedMenuItems = i.selectedMenuItems;

          // Parse the itemName summary (e.g. "2x Dadinho de Tapioca • 1x Caldinho de Feijão")
          // into a map of childItemId -> name, mirroring useEventPersistence logic.
          const summary = i.itemName || '';
          const parts = summary.split('•').map(p => p.trim()).filter(Boolean);
          const itemIds = Object.keys(i.selectedMenuItems);
          const resolvedNames: Record<string, string> = {};
          parts.forEach((part, idx) => {
            const cleanName = part.replace(/^\d+x\s*/, '').trim();
            if (cleanName && idx < itemIds.length) {
              resolvedNames[itemIds[idx]] = cleanName;
            }
          });
          if (Object.keys(resolvedNames).length === 0 && summary) {
            itemIds.forEach(id => { resolvedNames[id] = summary; });
          }
          base.selectedItemNames = resolvedNames;
        }

        if (i.categorySelections && Object.values(i.categorySelections).some(arr => arr.length > 0)) {
          base.categorySelections = i.categorySelections;
          // Proposta que mostra ao cliente só os itens escolhidos (+ adicionais com custo): a opção viaja no menu do evento.
          if (getQuoteOnlySelected()) base.showOnlySelected = true;
          const allNames: Record<string, string> = { ...(base.selectedItemNames || {}) };
          Object.values(i.categorySelections).forEach((items, catIdx) => {
            items.forEach((name, idx) => {
              allNames[`cat${catIdx}_${idx}`] = name;
            });
          });
          base.selectedItemNames = allNames;
        }
        // Horas adicionais escolhidas na proposta: sem isto o link, o checkout e a página da proposta perdem as horas e o valor delas.
        if ((Number(i.extraHours) || 0) > 0) {
          base.extraHours = Number(i.extraHours);
          base.hourExtensionAmount = Number(i.hourExtensionAmount) || 0;
        }
        if (i.selectedOptionalItems && i.selectedOptionalItems.length > 0) {
          base.selectedOptionalItems = i.selectedOptionalItems;
          // Opções por quantidade (ex.: Pizza sem Glúten, 4 pessoas): o cliente e o fornecedor veem as mesmas unidades.
          const quantities = normalizeOptionalQuantities(i.optionalQuantities);
          const chosen = Object.fromEntries(i.selectedOptionalItems.filter((id: string) => quantities[id]).map((id: string) => [id, quantities[id]]));
          if (Object.keys(chosen).length > 0) base.optionalQuantities = chosen;
        }
        return base;
      });

      // Build substitutions summary
      const allSubstitutions: Record<string, any> = {};
      for (const item of cartItems) {
        if (item.substitutions && Object.keys(item.substitutions).length > 0) {
          allSubstitutions[item.productId] = {
            productName: item.itemName || item.productName,
            substitutions: item.substitutions,
          };
        }
        if (item.additions && Object.keys(item.additions).length > 0) {
          allSubstitutions[item.productId] = {
            ...(allSubstitutions[item.productId] || { productName: item.itemName || item.productName }),
            additions: item.additions,
          };
        }
      }



      // Sem cupom/frete informados nesta gravação, o banco MANTÉM os valores já gravados (o RPC usa COALESCE):
      // o total precisa considerar esses mesmos valores. Antes ficava cupom de R$ 285 gravado e total sem o
      // desconto (VEI-4600: itens R$ 5.700, desconto R$ 285, total R$ 5.700).
      let persisted: { delivery_fee?: number | null; discount_value?: number | null } | null = null;
      if (!isCouponTouched(financialSummary) || financialSummary?.freight === undefined) {
        const { data } = await (supabase as any)
          .from("quote_drafts")
          .select("delivery_fee, discount_value")
          .eq("id", parentDraftId)
          .maybeSingle();
        persisted = data ?? null;
      }
      const { couponTouched, freight, discountValue, total: totalWithFreight } =
        resolveDraftTotals(subtotal, financialSummary, persisted);

      // Duração ("até X horas") e antecedência de chegada — congeladas no
      // rascunho pai pra sobreviverem à promoção do evento (ver promote_quote_draft_to_event).
      const firstItemWithHour = cartItems.find((i) => i.eventHourTime);
      const firstItemWithArrival = cartItems.find((i) => i.hourArrived);

      const { error: aggErr } = await callUpsertDraftRpc({
        p_id: parentDraftId,
        ...draftParentEventFields(cartItems),
        p_calculated_total: totalWithFreight,
        p_total_value: totalWithFreight,
        p_menu: menuSummary,
        p_selected_categories: allCategories,
        p_calculated_guest: pricePerGuest.toFixed(2),
        p_calculated_category: subtotal.toString(),
        p_item_quantity: cartItems.length.toString(),
        p_party_time: firstItemWithHour?.eventHourTime || null,
        p_event_hour_is_max: firstItemWithHour ? (firstItemWithHour.eventHourIsMax ?? null) : null,
        p_supplier_arrival_time: firstItemWithArrival?.hourArrived || null,
        // Send an explicit empty object when no substitutions remain. The RPC uses
        // COALESCE on updates, so `null` would preserve old substitutions instead
        // of clearing them after the supplier removes/troca an item.
        p_substitutions: allSubstitutions,
        // Cupom removido precisa ir como "vazio/0" (nulo manteria o cupom antigo gravado, pelo COALESCE do RPC).
        p_discount_code: couponTouched ? (financialSummary?.discount_code || '') : null,
        p_discount_value: couponTouched ? discountValue : null,
        p_delivery_fee: freight,
        p_is_update: true,
      });

      if (aggErr) {
        console.error("❌ Error updating draft parent aggregates:", aggErr.message);
      } else {
      }
      } catch (err) {
        console.error("❌ Error syncing cart to drafts:", err);
      }
    };

    const queuedSync = syncQueueRef.current.then(runSync, runSync);
    syncQueueRef.current = queuedSync.catch(() => undefined);
    await queuedSync;
  }, []);

  /**
   * Remove a child item from quote_drafts by origem (productId).
   */
  const removeDraftChildItem = useCallback(async (productId: string, parentDraftId: string) => {
    try {
      // Find children with this origem/productId. Note: origem might be "parent:child"
      const { data: existing } = await (supabase as any)
        .from("quote_drafts")
        .select("id")
        .eq("parent_event_id", parentDraftId)
        .eq("is_active", true)
        .or(`origem.eq.${productId},origem.like.${productId}:%`);

      if (existing && existing.length > 0) {

        
        // Use RPC to deactivate each child (to bypass RLS correctly)
        for (const item of existing) {
          const { error } = await callUpsertDraftRpc({
            p_id: item.id,
            p_is_active: false, // Explicitly deactivate
            p_status: "Removido",
            p_is_update: true,
          });
          if (error) console.warn("⚠️ Error removing draft child via RPC:", error);
        }
      }
    } catch (err) {
      console.error("❌ Error removing draft child:", err);
    }
  }, []);

  /**
   * Promote a draft to the `events` table, generating an event_code.
   * Returns { parentEventId, eventCode } on success.
   * On failure the reason is stored and can be read via `getLastPromoteError()`.
   */
  const promoteDraftToEvent = useCallback(async (
    draftParentId: string,
    supplierName?: string
  ): Promise<{ parentEventId: string; eventCode: string } | null> => {
    lastPromoteErrorRef.current = null;
    try {
      // Use SECURITY DEFINER RPC to bypass RLS issues
      // Envia o evento já conhecido para que editar/voltar na proposta atualize o mesmo código.
      const targetEventId = readTargetEventId();
      const { data, error } = await (supabase as any).rpc("promote_quote_draft_to_event", {
        p_draft_parent_id: draftParentId,
        p_supplier_name: supplierName || null,
        ...(targetEventId ? { p_target_event_id: targetEventId } : {}),
      });

      if (error) {
        console.error("❌ RPC promote_quote_draft_to_event failed:", error);
        throw error;
      }
      if (!data || !data.parent_event_id) throw new Error("RPC returned empty result");


      const parentEventId = data.parent_event_id;
      const eventCode = data.event_code;

      // O RPC histórico de promoção não possui event_type em quote_drafts.
      // Persiste o tipo escolhido no simulador diretamente no evento promovido,
      // mantendo o banco como fonte de verdade para links abertos em outra sessão.
      const storedEventType = sessionStorage.getItem('ameup_event_type')?.trim();
      // O marcador genérico "Evento" não é um tipo escolhido: não grava, para o passo ser exibido no infos-festa.
      const selectedEventType = isRealEventType(storedEventType) ? storedEventType : '';
      if (selectedEventType) {
        const { error: eventTypeError } = await (supabase as any)
          .from('events')
          .update({ event_type: selectedEventType, updated_at: new Date().toISOString() })
          .eq('id', parentEventId);

        if (eventTypeError) {
          console.error('❌ Could not persist event type on promoted quote:', eventTypeError);
          throw eventTypeError;
        }
      }

      // Categorias com preço especial (crianças, staff...) informadas no simulador.
      // `quote_drafts` não possui essa coluna, então gravamos direto no evento
      // promovido para que a proposta e o contrato mostrem o detalhamento.
      try {
        const storedCart = sessionStorage.getItem('supplier_cart_items');
        const cartItems: CartItem[] = storedCart ? JSON.parse(storedCart) : [];
        const snapshot = buildSpecialGuestsSnapshot(cartItems, (i) => getCartItemBasePricePerGuest(i, readSessionCheckoutTax()));
        // Adicional de frete: o rascunho guarda só o frete total (delivery_fee); o adicional vai direto no evento promovido.
        const extraColumns = freightExtraColumns(cartItems);
        if (Object.keys(extraColumns).length > 0) {
          const { error: extraErr } = await (supabase as any).from('events').update({ ...extraColumns, updated_at: new Date().toISOString() }).eq('id', parentEventId);
          if (extraErr) console.warn('⚠️ Could not persist freight extra on promoted quote:', extraErr);
        }
        if (Object.keys(snapshot).length > 0) {
          const { error: specialErr } = await (supabase as any)
            .from('events')
            .update({ special_guests: snapshot, updated_at: new Date().toISOString() })
            .eq('id', parentEventId);
          if (specialErr) {
            console.warn('⚠️ Could not persist special guests on promoted quote:', specialErr);
          }
        }
      } catch (e) {
        console.warn('⚠️ Could not build special guests snapshot:', e);
      }



      // Save client info via edge function (service role bypasses RLS for guests).
      // In quote mode the supplier typically fills only name + phone — persist
      // whatever is available so the shared link can greet the client by name.
      const clientName = sessionStorage.getItem('ameup_quote_client_name');
      const clientPhone = sessionStorage.getItem('ameup_quote_client_phone');
      const clientEmail = sessionStorage.getItem('ameup_quote_client_email');
      if (clientName) {
        // Fire-and-forget: salvar info do cliente não deve bloquear a geração do link.
        // O link compartilhável já tem o event_code; o nome do cliente é metadado
        // que pode ser persistido em background.
        void supabase.functions
          .invoke('save-event-client-info', {
            body: {
              eventId: parentEventId,
              clientName,
              clientEmail: clientEmail || null,
              clientPhone: clientPhone || null,
            },
          })
          .catch((e: unknown) => console.warn('⚠️ Could not save client info via edge function:', e));
      }

      // Update sessionStorage
      sessionStorage.setItem('ameup_parent_event_id', parentEventId);
      sessionStorage.setItem('ameup_event_code', eventCode);
      // O rascunho (draft) NÃO é descartado depois de promovido: um segundo compartilhamento (ex.: "Copiar" e depois
      // "WhatsApp") precisa reaproveitar o MESMO rascunho. Sem ele, o código criava um rascunho novo e a promoção
      // gerava OUTRO evento com outro código (a proposta aparecia duplicada no Kanban). Com o mesmo rascunho, a
      // promoção atualiza o evento já criado. O rascunho só é limpo ao começar uma nova proposta.

      return { parentEventId, eventCode };
    } catch (err) {
      const rawMessage =
        (err as { message?: string })?.message ||
        (typeof err === "string" ? err : "Erro desconhecido");
      const normalized = rawMessage.toLowerCase();

      let code: PromoteErrorCode = "unknown";
      if (normalized.includes("draft pai") || normalized.includes("nao encontrado") || normalized.includes("não encontrado")) {
        code = "draft_not_found";
      } else if (normalized.includes("sem itens") || normalized.includes("empty")) {
        code = "empty_quote";
      } else if (normalized.includes("failed to fetch") || normalized.includes("network")) {
        code = "network";
      }

      lastPromoteErrorRef.current = { code, message: rawMessage };
      console.error("❌ Error promoting draft to event:", code, rawMessage);

      // Stale draft id in session would make every retry fail the same way.
      if (code === "draft_not_found" || code === "empty_quote") {
        draftParentIdRef.current = null;
        sessionStorage.removeItem("ameup_draft_parent_id");
      }

      return null;
    }
  }, []);

  const getLastPromoteError = useCallback(() => lastPromoteErrorRef.current, []);

  const getDraftParentId = useCallback(() => draftParentIdRef.current, []);
  const setDraftParentId = useCallback((id: string | null) => {
    draftParentIdRef.current = id;
    if (id) sessionStorage.setItem('ameup_draft_parent_id', id);
    else sessionStorage.removeItem('ameup_draft_parent_id');
  }, []);

  return {
    upsertDraftParent,
    syncCartToDrafts,
    removeDraftChildItem,
    promoteDraftToEvent,
    getLastPromoteError,
    getDraftParentId,
    setDraftParentId,
  };
};

